// React
import { ReactElement } from 'react';
// Next
import { GetServerSideProps, NextPage } from 'next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
// Context
import { PageProviders } from '@utmb/contexts/index';
import { useUser } from '@utmb/contexts/AuthContext';
// Features
import { MyDashboardMemberHeader } from '@utmb/features/my-dashboard-header/MyDashboardMemberHeader';
import { MyDashboardOrganizerHeader } from '@utmb/features/my-dashboard-header/MyDashboardOrganizerHeader';
import { OrganizerInformation } from '@utmb/features/my-information/OrganizerInformation';
// Layout
import LayoutBase from '@utmb/layouts/my-utmb/LayoutBase';
// Interfaces
import { HomeView, LangEnum, PageDefaultProps } from '@utmb/types/index';
import { UserAccountTypeEnum } from '@utmb/types/enum/UserAccountTypeEnum';
// Utils
import isMobile from '@utmb/utils/is-mobile';
import { fetchAPI } from '@utmb/utils/utmb';
import { OrganizerAccountSettings } from '@utmb/features/my-information/OrganizerAccountSettings';
import { MyDashboardMember } from '@utmb/features/my-dashboard/MyDashboardMember';

import { EmailModalNotificationContainer } from '@utmb/features/my-information/EmailModalNotificationContainer';

interface PageProps extends PageDefaultProps, HomeView {}

const MyDashboard: NextPage<PageProps> = ({ event, eventsTopBar, footer, isMobile, mobileBottomBar, navigation, seo, partners, languages }): ReactElement => {
    const { me } = useUser();

    return (
        <PageProviders>
            <LayoutBase
                {...seo}
                isMobile={isMobile}
                eventsTopBar={eventsTopBar}
                mobileBottomBar={mobileBottomBar}
                navigation={navigation}
                footer={footer}
                landing={true}
                event={event}
                partners={partners}
                languages={languages}
                menuLogoIsDark={true}
            >
                {me?.accountType === UserAccountTypeEnum.ORGANIZER && (
                    <>
                        <MyDashboardOrganizerHeader />
                        <OrganizerInformation />
                        <OrganizerAccountSettings />
                    </>
                )}
                {(!me || me.accountType === UserAccountTypeEnum.MEMBER) && (
                    <>
                        <MyDashboardMemberHeader />
                        <MyDashboardMember />
                        <EmailModalNotificationContainer />
                    </>
                )}
            </LayoutBase>
        </PageProviders>
    );
};

export default MyDashboard;

export const getServerSideProps: GetServerSideProps<HomeView> = async ({ locale, defaultLocale, req }) => {
    try {
        // Translate
        const lang: LangEnum = (locale || defaultLocale || i18n.defaultLocale) as LangEnum;
        const translations = await serverSideTranslations(lang, ['common', 'myutmb']);

        // Page Content
        const page = await fetchAPI(req, '/content/home', { lang });

        // if error
        if (!page) return { notFound: true };
        const { pageHeader, seo, slices } = page;

        return {
            props: {
                isMobile: isMobile(req),
                locale: lang,
                pageHeader,
                seo,
                slices,
                ...translations,
            },
        };
    } catch (error) {
        return {
            notFound: true,
        };
    }
};
